import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  Bell,
  CheckCircle,
  Clock,
  FileText,
  Globe,
  Heart,
  MapPin,
  Plane,
  ShieldCheck,
  Smartphone,
  Star,
  Users,
} from "lucide-react";
import { StoreButtons } from "@/components/landing/StoreButtons";
import { Button } from "@/components/ui/button";
import { home } from "@/content/home";
import type { Locale } from "@/i18n/locales";
import { localePath } from "@/i18n/locales";
import { AUTH_API_BASE_URL } from "@/lib/auth-api";

type HomeViewProps = {
  locale: Locale;
};

type HomeStatsCard = {
  key: string;
  value: number | string;
  label: string;
  sub: string;
};

type HomeStatsPayload = {
  lastUpdated: string;
  cards: HomeStatsCard[];
  summary: {
    countryCodes: number;
    pushSent30d: number;
    emailFallback30d: number;
  };
  flu: {
    code: string | null;
    date: string | null;
    label: string;
    nudge: string | null;
  };
};

type HomeStatsResponse = {
  success: boolean;
  data?: HomeStatsPayload;
};

const partners = [
  { src: "/images/partners/ggd.webp", alt: "GGD", width: 300, height: 150 },
  { src: "/images/partners/erasmus.webp", alt: "Erasmus MC", width: 300, height: 150 },
  { src: "/images/partners/ncj.webp", alt: "NCJ", width: 300, height: 150 },
  {
    src: "/images/partners/ggd-app-store.webp",
    alt: "GGD App Store",
    width: 300,
    height: 150,
  },
  {
    src: "/images/partners/covid-test-loket.webp",
    alt: "Covid Test Loket",
    width: 300,
    height: 150,
  },
];

const statIcons = [Users, Star, Globe];
const featureIcons = [Plane, FileText, Users, Bell, MapPin];
const nlInsightIcons = [Clock, Bell, ShieldCheck];
const nlFallbackInsights: HomeStatsCard[] = [
  {
    key: "expiringSoon",
    value: "—",
    label: "vaccins verlopen binnen 30 dagen",
    sub: "",
  },
  {
    key: "notifications30d",
    value: "—",
    label: "notificaties in 30 dagen",
    sub: "",
  },
  {
    key: "trackedVaccinations",
    value: "—",
    label: "vaccinaties geregistreerd",
    sub: "",
  },
];
const featureImages = [
  "/images/features/travel.webp",
  "/images/features/records.webp",
  "/images/features/family.webp",
  "/images/features/reminders.webp",
  "/images/features/providers.webp",
];
const featureImageSizes = [
  { width: 600, height: 300 },
  { width: 1200, height: 675 },
  { width: 1200, height: 600 },
  { width: 1200, height: 900 },
  { width: 1200, height: 1112 },
] as const;
const howImageSizes = [
  { width: 800, height: 440 },
  { width: 1200, height: 400 },
  { width: 400, height: 220 },
  { width: 1200, height: 400 },
] as const;
const featureColors = [
  "bg-primary/10 text-primary",
  "bg-accent/10 text-accent",
  "bg-primary/10 text-primary",
  "bg-accent/10 text-accent",
  "bg-primary/10 text-primary",
];
// ponytail: same path for all locales; content titles stay localized
const featureHrefs = [
  "/travel",
  "/app",
  "/app",
  "/booster-reminders",
  "/app",
] as const;
const whyIcons = [Users, Plane, Heart];
const travelIcons = [Globe, ShieldCheck, Clock];
const howImages = [
  "/images/how-it-works/01.webp",
  "/images/how-it-works/02.webp",
  "/images/how-it-works/03.webp",
  "/images/how-it-works/04.webp",
];
const testimonialPhotos = [
  "/images/testimonials/rafael.webp",
  "/images/testimonials/lotte.webp",
  "/images/testimonials/sonja.webp",
  "/images/testimonials/jeroen.webp",
  "/images/testimonials/marieke.webp",
  "/images/testimonials/hans.webp",
];

const highlightStyle = {
  background: "linear-gradient(135deg, #0ea5c9 0%, #0369a1 60%, #1a3a5c 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
} as const;

function scrollToDownload() {
  document.querySelector("#download")?.scrollIntoView({ behavior: "smooth" });
}

export function HomeView({ locale }: HomeViewProps) {
  const copy = home[locale];
  const [homeStats, setHomeStats] = useState<HomeStatsPayload | null>(null);

  useEffect(() => {
    if (locale !== "nl") {
      return;
    }

    const controller = new AbortController();

    async function loadHomeStats() {
      try {
        const response = await fetch(`${AUTH_API_BASE_URL}/home/stats`, {
          headers: {
            Accept: "application/json",
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to load home stats: ${response.status}`);
        }

        const payload = (await response.json()) as HomeStatsResponse;
        if (payload.success && payload.data) {
          setHomeStats(payload.data);
        }
      } catch (error) {
        if ((error as { name?: string }).name === "AbortError") {
          return;
        }
      }
    }

    void loadHomeStats();

    return () => controller.abort();
  }, [locale]);

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(locale === "nl" ? "nl-NL" : "en-GB"),
    [locale],
  );

  const nlStats = useMemo<HomeStatsCard[]>(() => {
    if (locale !== "nl") {
      return [];
    }

    if (!homeStats) {
      return nlFallbackInsights;
    }

    return homeStats.cards.map((card) => ({
      ...card,
      sub: "",
      value:
        typeof card.value === "number"
          ? numberFormatter.format(card.value)
          : card.value,
    }));
  }, [homeStats, locale, numberFormatter]);

  const fluSummary = homeStats?.flu ?? null;

  return (
    <main className="overflow-hidden bg-background">
      <section className="relative flex min-h-screen items-center overflow-hidden pb-0 pt-24 md:pt-32">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                "radial-gradient(circle, #1a7fa8 1px, transparent 1px)",
              backgroundSize: "28px 28px",
            }}
          />
          <div
            className="absolute -right-60 -top-60 h-[900px] w-[900px] rounded-full blur-3xl"
            style={{
              background:
                "radial-gradient(circle, hsl(199 85% 40% / 0.18) 0%, transparent 70%)",
            }}
          />
          <div
            className="absolute -left-48 top-1/2 h-[600px] w-[600px] rounded-full blur-3xl"
            style={{
              background:
                "radial-gradient(circle, hsl(38 95% 52% / 0.10) 0%, transparent 70%)",
            }}
          />
          <div
            className="absolute bottom-0 right-1/4 h-[300px] w-[400px] rounded-full blur-3xl"
            style={{
              background:
                "radial-gradient(circle, hsl(199 85% 40% / 0.08) 0%, transparent 70%)",
            }}
          />
        </div>

        <div className="relative mx-auto w-full max-w-6xl px-5 py-16">
          <div className="grid items-center gap-16 lg:grid-cols-2">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-6"
              >
                <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/8 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary shadow-sm">
                  <Smartphone className="h-3.5 w-3.5" />
                  {copy.hero.badge}
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="font-heading text-5xl font-bold leading-[1.05] tracking-tight md:text-6xl lg:text-7xl"
              >
                <span className="text-[#1a3a5c]">{copy.hero.h1.prefix} </span>
                <br />
                <span style={highlightStyle}>{copy.hero.h1.highlight}</span>
                <br />
                <span className="text-[#1a3a5c]">{copy.hero.h1.suffix}</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mt-6 max-w-lg text-lg leading-relaxed text-muted-foreground"
              >
                {copy.hero.sub}
              </motion.p>

              <motion.ul
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.28 }}
                className="mt-6 space-y-2.5"
              >
                {copy.hero.bullets.map((bullet) => (
                  <li
                    key={bullet}
                    className="flex items-center gap-2.5 text-sm font-medium text-foreground"
                  >
                    <CheckCircle className="h-4 w-4 flex-shrink-0 text-primary" />
                    {bullet}
                  </li>
                ))}
              </motion.ul>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.35 }}
                className="mt-10"
              >
                <StoreButtons locale={locale} />
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              className="relative hidden items-end justify-center lg:flex"
            >
              <div
                className="absolute bottom-0 left-1/2 -z-10 h-[28rem] w-[28rem] -translate-x-1/2 rounded-full blur-3xl"
                style={{
                  background:
                    "radial-gradient(circle, hsl(199 85% 40% / 0.22) 0%, transparent 70%)",
                }}
              />
              <img
                src="/images/phone.webp"
                alt={copy.hero.phoneAlt}
                width={667}
                height={1024}
                fetchPriority="high"
                className="relative h-auto w-[26rem] max-w-full drop-shadow-2xl lg:-my-12"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-6xl px-5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 overflow-hidden rounded-sm border border-border bg-border shadow-sm sm:grid-cols-3 sm:gap-px"
          >
            {copy.stats.map((stat, index) => {
              const Icon = statIcons[index] ?? Users;
              return (
                <div
                  key={stat.label}
                  className="group relative flex items-center gap-4 bg-card px-8 py-8 transition-colors hover:bg-primary/[0.03]"
                >
                  <div className="absolute bottom-0 left-0 top-0 w-1 bg-primary opacity-0 transition-opacity group-hover:opacity-100" />
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-sm bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-heading text-2xl font-bold leading-none text-[#1a3a5c] md:text-3xl">
                      {stat.value}
                    </div>
                    <div className="mt-0.5 text-sm font-semibold text-foreground">
                      {stat.label}
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {stat.sub}
                    </div>
                  </div>
                </div>
              );
            })}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-12"
          >
            <p className="mb-8 text-center text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {copy.partnersHeading}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-10 md:gap-16">
              {partners.map((partner) => (
                <img
                  key={partner.src}
                  src={partner.src}
                  alt={partner.alt}
                  width={partner.width}
                  height={partner.height}
                  loading="lazy"
                  className="h-11 object-contain opacity-50 grayscale transition-opacity hover:opacity-80 hover:grayscale-0 md:h-16"
                />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {locale === "nl" ? (
        <section className="pb-20 pt-4 md:pb-28">
          <div className="mx-auto max-w-6xl px-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/8 px-4 py-1 text-xs font-bold uppercase tracking-widest text-primary">
                    Nederland
                  </span>
                  <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-[#1a3a5c] md:text-5xl">
                    Statistieken
                  </h2>
                  <p className="mt-3 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
                    Een feitelijk inzicht van wat nu relevant is voor gezinnen,
                    reizigers en herinneringen in Nederland.
                  </p>
                </div>
                <div className="inline-flex items-center gap-2 self-start rounded-full border border-border bg-card px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground shadow-sm">
                  <Clock className="h-3.5 w-3.5 text-primary" />
                  Wekelijks vernieuwd
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {nlStats.map((insight, index) => {
                  const Icon = nlInsightIcons[index] ?? Globe;

                  return (
                    <div
                      key={insight.label}
                      className="group rounded-2xl border border-border/60 bg-card p-6 transition-all hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5"
                    >
                      <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <div className="font-heading text-3xl font-bold leading-none text-[#1a3a5c]">
                        {insight.value}
                      </div>
                      <div className="mt-2 text-sm font-semibold text-foreground">
                        {insight.label}
                      </div>
                      {insight.sub ? (
                        <div className="mt-1 text-sm leading-relaxed text-muted-foreground">
                          {insight.sub}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              {fluSummary ? (
                <div className="mt-4 rounded-2xl border border-border/60 bg-card px-5 py-4 text-sm leading-relaxed text-muted-foreground">
                  <span className="font-semibold text-foreground">
                    Nederlandse griepstatus:
                  </span>{" "}
                  {fluSummary.label}
                  {fluSummary.nudge ? ` - ${fluSummary.nudge}` : ""}
                </div>
              ) : null}
            </motion.div>
          </div>
        </section>
      ) : null}

      <section id="why" className="bg-secondary/40 py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-5">
          <div className="mx-auto mb-16 max-w-2xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/8 px-4 py-1 text-xs font-bold uppercase tracking-widest text-primary">
              {copy.why.eyebrow}
            </span>
            <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-[#1a3a5c] md:text-5xl">
              {copy.why.heading}
            </h2>
            <p className="mt-4 whitespace-pre-line text-lg text-muted-foreground">
              {copy.why.paragraph}
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-3">
            {copy.why.items.map((item, index) => {
              const Icon = whyIcons[index] ?? Heart;
              return (
                <div key={item.title} className="flex gap-4">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-[#1a3a5c]">
                      {item.title}
                    </h3>
                    <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground">
                      {item.text}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="features" className="py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-5">
          <div className="mx-auto mb-16 max-w-2xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/8 px-4 py-1 text-xs font-bold uppercase tracking-widest text-primary">
              {copy.features.eyebrow}
            </span>
            <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-[#1a3a5c] md:text-5xl">
              {copy.features.heading}
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              {copy.features.paragraph}
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {copy.features.items.map((item, index) => {
              const Icon = featureIcons[index] ?? ShieldCheck;
              const href = featureHrefs[index] ?? "/app";
              return (
                <motion.article
                  key={item.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group overflow-hidden rounded-2xl border border-border/50 bg-white/70 backdrop-blur-sm transition-all duration-300 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/8"
                  style={{
                    boxShadow:
                      "0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
                  }}
                >
                  <Link
                    to={localePath(locale, href)}
                    className="block h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                  >
                    <img
                      src={featureImages[index]}
                      alt={item.title}
                      width={featureImageSizes[index]?.width ?? 1200}
                      height={featureImageSizes[index]?.height ?? 600}
                      loading="lazy"
                      className="h-40 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="p-7">
                      <div
                        className={`mb-4 flex h-10 w-10 items-center justify-center rounded-xl ${featureColors[index]}`}
                      >
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="mb-2 font-heading text-lg font-semibold">
                        {item.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        {item.description}
                      </p>
                    </div>
                  </Link>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      <section id="travel" className="bg-secondary/50 py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-5">
          <div className="grid items-center gap-16 lg:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-sm font-semibold uppercase tracking-wider text-accent">
                {copy.travel.eyebrow}
              </span>
              <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight md:text-4xl">
                {copy.travel.heading}
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                {copy.travel.paragraph}
              </p>
              <div className="mt-10 space-y-6">
                {copy.travel.highlights.map((item, index) => {
                  const Icon = travelIcons[index] ?? Globe;
                  return (
                    <div key={item.title} className="flex gap-4">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-accent/10">
                        <Icon className="h-5 w-5 text-accent" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold">
                          {item.title}
                        </h3>
                        <p className="mt-0.5 text-sm text-muted-foreground">
                          {item.text}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex justify-center"
            >
              <div className="relative w-full max-w-md">
                <div className="absolute inset-0 scale-105 rounded-3xl bg-gradient-to-br from-primary/20 to-accent/20 blur-2xl" />
                <div className="group relative rounded-3xl border border-border bg-card p-8 shadow-xl transition-[border-color,box-shadow,transform] hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-2xl">
                  <Link
                    to={localePath(locale, "/travel")}
                    className="absolute inset-0 z-0 rounded-3xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    aria-label={copy.travel.heading}
                  />
                  <img
                    src="/images/travel/destination.webp"
                    alt={copy.travel.card.imageAlt}
                    width={600}
                    height={400}
                    loading="lazy"
                    className="relative z-10 pointer-events-none mb-6 h-48 w-full rounded-2xl object-cover"
                  />
                  <div className="relative z-10 space-y-3 pointer-events-none">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-foreground">
                        {copy.travel.card.destination}
                      </span>
                      <Link
                        to={localePath(locale, "/travel/thailand")}
                        className="pointer-events-auto text-sm font-medium text-primary hover:underline"
                      >
                        {copy.travel.card.place} 🇹🇭
                      </Link>
                    </div>
                    <div className="h-px bg-border" />
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-foreground">
                        {copy.travel.card.recommended}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {copy.travel.card.count}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {copy.travel.card.vaccines.map((vaccine) => (
                        <span
                          key={vaccine}
                          className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-foreground"
                        >
                          {vaccine}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-5">
          <div className="mx-auto mb-14 max-w-2xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/8 px-4 py-1 text-xs font-bold uppercase tracking-widest text-primary">
              {copy.howItWorks.eyebrow}
            </span>
            <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight md:text-5xl">
              {copy.howItWorks.heading}
            </h2>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {copy.howItWorks.steps.map((step, index) => (
              <motion.article
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
              >
                {index < copy.howItWorks.steps.length - 1 ? (
                  <div className="absolute left-[calc(100%-1rem)] top-7 z-10 hidden h-px w-8 bg-border lg:block" />
                ) : null}
                <div className="h-full overflow-hidden rounded-2xl border border-border/60 bg-card">
                  <img
                    src={howImages[index]}
                    alt=""
                    width={howImageSizes[index]?.width ?? 1200}
                    height={howImageSizes[index]?.height ?? 400}
                    loading="lazy"
                    className="h-36 w-full object-cover"
                  />
                  <div className="p-6">
                    <div
                      className="mb-3 font-heading text-3xl font-bold opacity-35"
                      style={{
                        background:
                          "linear-gradient(135deg, #0ea5c9 0%, #1a3a5c 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                      }}
                    >
                      {step.number}
                    </div>
                    <h3 className="mb-2 font-heading font-semibold">
                      {step.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {step.description}
                    </p>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-secondary/40 py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-5">
          <div className="mx-auto mb-14 max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-primary">
              {copy.testimonials.eyebrow}
            </span>
            <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight md:text-5xl">
              {copy.testimonials.heading}
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              {copy.testimonials.paragraph}
            </p>
          </div>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {copy.testimonials.items.map((item, index) => (
              <motion.article
                key={item.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                className="flex flex-col gap-4 rounded-2xl border border-border/60 bg-card p-6"
              >
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star
                      key={n}
                      className="h-4 w-4 fill-primary text-primary"
                    />
                  ))}
                </div>
                <p className="flex-1 text-sm leading-relaxed text-foreground">
                  “{item.quote}”
                </p>
                <div className="flex items-center gap-3 border-t border-border pt-2">
                  <img
                    src={testimonialPhotos[index]}
                    alt=""
                    width={320}
                    height={320}
                    loading="lazy"
                    className="h-10 w-10 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-heading text-sm font-semibold">
                      {item.name}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {item.role}
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section id="community" className="py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-5">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary/90 to-primary"
          >
            <div className="absolute right-0 top-0 h-64 w-64 -translate-y-1/2 translate-x-1/3 rounded-full bg-white/10" />
            <div className="absolute bottom-0 left-0 h-48 w-48 -translate-x-1/4 translate-y-1/3 rounded-full bg-white/10" />
            <div className="relative px-8 py-16 text-center md:px-16 md:py-20">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2">
                <Heart className="h-4 w-4 text-white" />
                <span className="text-sm font-medium text-white">
                  {copy.community.eyebrow}
                </span>
              </div>
              <h2 className="mx-auto max-w-2xl font-heading text-3xl font-bold tracking-tight text-primary-foreground md:text-5xl">
                {copy.community.heading}
              </h2>
              <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-primary-foreground/80">
                {copy.community.paragraph}
              </p>
              <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <Button
                  size="lg"
                  className="h-12 rounded-full bg-white px-8 text-base font-semibold text-foreground shadow-lg hover:bg-white/90"
                  onClick={scrollToDownload}
                >
                  {copy.community.cta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
              <div className="mt-12 flex items-center justify-center gap-3">
                <div className="flex -space-x-3">
                  {testimonialPhotos.slice(0, 4).map((src) => (
                    <img
                      key={src}
                      src={src}
                      alt=""
                      width={320}
                      height={320}
                      loading="lazy"
                      className="h-10 w-10 rounded-full border-2 border-primary object-cover"
                    />
                  ))}
                </div>
                <p className="text-sm text-primary-foreground/80">
                  <span className="font-semibold text-primary-foreground">
                    {copy.community.proofCount}
                  </span>{" "}
                  {copy.community.proof}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="download" className="bg-secondary/50 pt-20 pb-0 md:pt-28">
        <div className="mx-auto max-w-6xl px-5">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2">
              <Smartphone className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">{copy.download.badge}</span>
            </div>
            <h2 className="mx-auto max-w-2xl font-heading text-3xl font-bold tracking-tight md:text-5xl">
              {copy.download.heading}
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-muted-foreground">
              {copy.download.paragraph}
            </p>
            <StoreButtons locale={locale} className="mt-10 justify-center" />
            <p className="mt-6 text-sm text-muted-foreground">
              {copy.download.note}
            </p>
            <img
              src="/images/app/icon-phone.webp"
              alt={copy.download.iconAlt}
              width={532}
              height={737}
              loading="lazy"
              className="mx-auto mt-14 block w-full max-w-xs drop-shadow-2xl"
            />
          </motion.div>
        </div>
      </section>
    </main>
  );
}
